import type { ReactNode } from 'react';

type Tone = 'emerald' | 'sky' | 'amber' | 'rose' | 'slate' | 'teal';

const toneClasses: Record<Tone, string> = {
  emerald: 'bg-agrida-50 text-agrida-800',
  sky: 'bg-sky-50 text-sky-800',
  amber: 'bg-amber-50 text-amber-800',
  rose: 'bg-rose-50 text-rose-800',
  slate: 'bg-slate-50 text-slate-800',
  teal: 'bg-teal-50 text-teal-800',
};

export function MobileMetricCard({
  label,
  value,
  caption,
  icon,
  tone = 'slate',
  onClick,
}: {
  label: string;
  value: ReactNode;
  caption?: ReactNode;
  icon?: string;
  tone?: Tone;
  onClick?: () => void;
}) {
  const content = (
    <>
      <div className="flex items-center justify-center gap-1.5">
        {icon && <i className={`${icon} shrink-0 text-sm opacity-70`} />}
        <p className="min-w-0 text-center text-[11px] font-medium leading-tight opacity-70">{label}</p>
      </div>
      <p className="mt-1 text-center text-[15px] font-bold leading-tight text-safe-normal">{value}</p>
      {caption && <p className="metric-caption mt-1 text-center text-[10px] leading-snug opacity-65">{caption}</p>}
    </>
  );

  const className = `metric-card flex min-h-[88px] flex-col justify-center rounded-2xl p-3 text-center ${toneClasses[tone]}`;
  if (onClick) {
    return <button type="button" onClick={onClick} className={`${className} w-full transition active:scale-[0.98]`}>{content}</button>;
  }
  return <div className={className}>{content}</div>;
}

export function MobileSectionHeader({
  title,
  count,
  actionLabel,
  onAction,
}: {
  title: string;
  count?: ReactNode;
  actionLabel?: string;
  onAction?: () => void;
}) {
  return (
    <div className="mb-2.5 flex min-w-0 items-center justify-between gap-3">
      <div className="min-w-0">
        <h3 className="truncate text-sm font-semibold text-slate-900">{title}</h3>
        {count !== undefined && <p className="metric-caption mt-0.5 text-[10px] text-slate-400">{count}</p>}
      </div>
      {actionLabel && onAction && (
        <button type="button" onClick={onAction} className="shrink-0 text-xs font-semibold text-agrida-700">
          {actionLabel} <i className="ri-arrow-right-s-line" />
        </button>
      )}
    </div>
  );
}

export function MobileTabs<T extends string>({
  tabs,
  value,
  onChange,
  equal = false,
}: {
  tabs: { id: T; label: string; icon?: string }[];
  value: T;
  onChange: (value: T) => void;
  equal?: boolean;
}) {
  return (
    <div className={`${equal ? 'grid grid-cols-3' : 'hide-scrollbar flex overflow-x-auto'} gap-1 rounded-2xl bg-slate-100 p-1`}>
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          onClick={() => onChange(tab.id)}
          className={`flex min-h-11 min-w-0 items-center justify-center gap-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition ${equal ? 'w-full px-1.5' : 'shrink-0 px-3'} ${
            value === tab.id ? 'bg-white text-agrida-700 shadow-sm' : 'text-slate-500'
          }`}
        >
          {tab.icon && <i className={tab.icon} />}
          {tab.label}
        </button>
      ))}
    </div>
  );
}
